import React, { useEffect, useState, useRef } from 'react';
import { useTutorial } from '../../context/TutorialContext';
import './TutorialOverlay.css';

const TutorialOverlay = () => {
  const { tutorialActive, currentStep, steps, nextStep, completeTutorial } = useTutorial();
  // eslint-disable-next-line no-unused-vars
  const [highlightedElement, setHighlightedElement] = useState(null);
  const [elementRect, setElementRect] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!tutorialActive) return;

    const currentStepData = steps[currentStep];
    if (!currentStepData) return;

    // Find the target element
    const targetElement = document.querySelector(currentStepData.target);
    
    if (targetElement) {
      setHighlightedElement(targetElement);
      
      // Get element position
      const rect = targetElement.getBoundingClientRect();
      setElementRect(rect);

      // Scroll element into view smoothly
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Add highlight class
      targetElement.classList.add('tutorial-highlight');

      // Play audio if available
      if (currentStepData.audioFile && audioRef.current) {
        audioRef.current.src = currentStepData.audioFile;
        audioRef.current.play().then(() => {
          setAudioPlaying(true);
        }).catch(err => {
          console.log('Audio playback failed:', err);
        });
      }

      // Listen for click on the highlighted element
      const handleElementClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Remove highlight
        targetElement.classList.remove('tutorial-highlight');
        
        // Move to next step
        nextStep();
      };

      targetElement.addEventListener('click', handleElementClick, { capture: true });

      // Cleanup
      return () => {
        targetElement.classList.remove('tutorial-highlight');
        targetElement.removeEventListener('click', handleElementClick, { capture: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const currentAudio = audioRef.current;
        if (currentAudio) {
          currentAudio.pause();
          setAudioPlaying(false);
        }
      };
    }
  }, [tutorialActive, currentStep, steps, nextStep]);

  if (!tutorialActive || !elementRect) return null;

  const currentStepData = steps[currentStep];

  // Calculate tooltip position
  const tooltipStyle = {
    top: elementRect.bottom + window.scrollY + 20,
    left: elementRect.left + (elementRect.width / 2),
    transform: 'translateX(-50%)'
  };

  return (
    <>
      {/* Dark overlay with cutout */}
      <div className="tutorial-overlay">
        {/* Spotlight effect on element */}
        <div 
          className="tutorial-spotlight"
          style={{
            top: elementRect.top + window.scrollY - 10,
            left: elementRect.left - 10,
            width: elementRect.width + 20,
            height: elementRect.height + 20
          }}
        />
      </div>

      {/* Tooltip with instructions */}
      <div className="tutorial-tooltip" style={tooltipStyle}>
        <div className="tutorial-tooltip-content">
          <h3 className="tutorial-tooltip-title">
            {currentStepData.title}
          </h3>
          <p className="tutorial-tooltip-description">
            {currentStepData.description}
          </p>
          <p className="tutorial-tooltip-action">
            👆 Go ahead, give it a click! I promise it won't bite 😄
          </p>
          
          {/* Progress indicator */}
          <div className="tutorial-progress">
            <span className="tutorial-progress-text">
              Step {currentStep + 1} of {steps.length}
            </span>
            <div className="tutorial-progress-bar">
              <div 
                className="tutorial-progress-fill"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Skip button */}
          <button 
            className="tutorial-skip-btn"
            onClick={completeTutorial}
          >
            I'm good, thanks Rainbow! 😊
          </button>
        </div>

        {/* Arrow pointing to element */}
        <div className="tutorial-arrow" />
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} style={{ display: 'none' }} />
    </>
  );
};

export default TutorialOverlay;
